/**
 * Форма `config/sources/<brand>.yaml` — реестр источников мониторинга (Б1.7 / Б2).
 * Норма — `docs/phase-2/01-monitoring.md` §7.1; факты о живых источниках —
 * `docs/sources.md` (замер 22.08.2026, ПРАВИЛА 1.1–6.26).
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ ФАЙЛ, А НЕ КЛЮЧ `sources:` В КОНФИГЕ БРЕНДА (решение §7.1):
 * конфиг бренда грузит ПУБЛИКАТОР, и опечатка в реестре источников роняла бы разбор
 * его файла — то есть останавливала публикацию. Мониторинг и публикация не имеют
 * права падать вместе. Цена — связь по соглашению `config/sources/<brand.id>.yaml`.
 *
 * Разбор YAML живёт у потребителей (`yaml` объявлен у brand-config и tools —
 * §Окружение 2); здесь только форма. Проекцию файла в таблицы source/brand_source
 * пишет `npm run sources:sync` (05-seams §8.2) — бэкенд остаётся единственным
 * писателем БД.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { PARSER_KINDS } from '../sources/parser.ts';
import { SOURCE_CODE_RE } from '../sources/source-code.ts';
import { AllowedHost } from './brand-config.ts';

/** Код источника — ключ таблицы source; живёт листом графа (`sources/source-code.ts`). */
export { SOURCE_CODE_RE };

/**
 * Чей голос у источника (AGI Boardroom, решение Ильи 2026-11-02):
 *   · `official` — организация сообщает СВОИ новости и исследования: лаборатории, вендоры,
 *     институты, университеты, регуляторы, собственные отчёты консультантов;
 *   · `media` — все, кто пересказывает чужие новости: издания, агрегаторы, Telegram-каналы.
 * По нему писатель выбирает адрес ссылки ВНУТРИ текста (`source_link: 'inline'`): ссылка ведёт
 * только на первоисточник, и нет официального материала — нет и ссылки. Умолчание `media`
 * безопасно: строка без пометки ссылки не получит, а не получит чужую.
 */
export const SOURCE_ORIGINS = ['official', 'media'] as const;
export type SourceOrigin = (typeof SOURCE_ORIGINS)[number];
export const SourceOriginSchema = z.enum(SOURCE_ORIGINS);

/** Имя публичного канала Telegram: буквы/цифры/подчёркивание, 5–32 (форма t.me). */
const TME_CHANNEL_RE = /^[A-Za-z][A-Za-z0-9_]{4,31}$/;

const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * Адрес реестра. Жёстко по §7.1: схема ровно `https:`, hostname непустой, никаких
 * `http://`, `file://` и IP-литералов. Приватные диапазоны, скрытые за DNS-именем,
 * схема поймать не может — их отбивает рантайм-проверка перед каждым запросом
 * (ПРАВИЛО О-4, §7.6): резолв и сверка с приватными сетями до открытия сокета.
 * Односоставное имя (`localhost`) отбивается требованием точки — как в AllowedHost.
 */
export const SourceUrl = z.string().refine(
  (raw) => {
    let u: URL;
    try {
      u = new URL(raw);
    } catch {
      return false;
    }
    if (u.protocol !== 'https:') return false;
    const host = u.hostname;
    if (host === '' || !host.includes('.')) return false;
    if (IPV4_RE.test(host) || host.includes(':') || host.startsWith('[')) return false;
    return true;
  },
  { error: 'ожидался абсолютный https://-адрес с доменным именем (не IP-литерал)' },
);

/**
 * Звено цепочки. `chain` — НЕ «fallback при ошибке» (§7.1): это упорядоченный список
 * способов добраться до материалов, и следующее звено берётся ТОЛЬКО при исходах
 * `not_this_parser` и `stub`. При `timeout`/`http_error`/`network_error` цепочка не
 * переключается: отказ доступа — не отказ формата, и второй адрес лишь удвоил бы
 * нагрузку на лежащий источник.
 */
export const SourceChainLinkSchema = z.strictObject({
  parser: z.enum(PARSER_KINDS),
  url: SourceUrl,
  /** Только для `tme_s`: ожидаемый префикс data-post, он же имя канала (§6.3.2). */
  expect_prefix: z.string().regex(TME_CHANNEL_RE).optional(),
  /**
   * Только для `html`: селектор ссылок листинга. Исторически первое и единственное поле
   * разбора; сохранено как синоним `link_selector` — реестры с ним не переписываются.
   */
  list_selector: z.string().min(1).optional(),
  /** Только для `html`: режим разбора страницы. */
  mode: z.literal('listing').optional(),
  /**
   * Только для `html`. Селекторы карточки материала. Все допускают перечисление через
   * запятую: на одной странице форм карточки бывает две (у AthenaHQ сеточная `a.blog-item`
   * и отдельная `a.featured-article`), и второй селектор дешевле второго звена цепочки.
   *
   * Без них парсер работает эвристикой (ближайший блочный предок ссылки того же хоста), но
   * эвристика — не приёмка: у включённых источников селекторы прописываются явно.
   */
  item_selector: z.string().min(1).optional(),
  link_selector: z.string().min(1).optional(),
  title_selector: z.string().min(1).optional(),
  date_selector: z.string().min(1).optional(),
  lead_selector: z.string().min(1).optional(),
  /** Атрибут с датой у `date_selector`; пусто — берётся текст узла. */
  date_attr: z.string().min(1).optional(),
});

/**
 * Сетевые настройки, обязательные в `defaults` и перекрываемые на источнике.
 * Значения — не украшение: max_bytes держит MegaGroup на 13.4 МБ (sources.md §4),
 * min_interval_ms — вежливость ПРАВИЛА 5.2, retries_in_run/backoff_ms —
 * ПРАВИЛО 6.16 (3 попытки с backoff внутри прогона, только на transient-исходы).
 */
const NETWORK_KNOBS = {
  timeout_ms: z.int().positive(),
  max_bytes: z.int().positive(),
  min_interval_ms: z.int().positive(),
  max_requests_per_run: z.int().positive(),
  min_article_chars: z.int().positive(),
  fallback_charset: z.string().min(1),
  retries_in_run: z.int().min(1).max(10),
  backoff_ms: z.array(z.int().positive()).min(1),
};

/**
 * Перекрытие окна, сутки (sources.md ПРАВИЛО 6.29). Обход смотрит не только элементы новее
 * курсора, но и элементы, датированные до `overlap_days` суток РАНЬШЕ него: из них
 * допускаются те, чей канонический адрес не известен памяти дедупа. Так ловятся
 * опоздавшие — материалы, чья дата раньше их появления в ленте (ПРАВИЛО 4b.3, форма 2), и
 * второй материал дня у листинга, который печатает только день (ПРАВИЛО 4b.2).
 *
 * Значение — свойство ИСТОЧНИКА (насколько его дата отстаёт от выхода), а не расписания:
 * ПРАВИЛО 6.3 литерал допускает. 7 суток покрывают замеренное опоздание Data Insight (0–5
 * суток) с запасом. Потолок 28 — половина окна дедупа по умолчанию (56 суток): зона
 * перекрытия обязана целиком лежать в памяти, по которой узнаются повторы. 0 — перекрытия
 * нет, отсечка строго по курсору.
 */
export const OVERLAP_DAYS_DEFAULT = 7;
export const OVERLAP_DAYS_MAX = 28;
const OverlapDays = z.int().min(0).max(OVERLAP_DAYS_MAX);

/**
 * Политика выборки строки (ревью 18.10.2026, №3, №14, №16; P7 п. 3). Всё ниже — свойство
 * ИСТОЧНИКА, а не кода: где лежит текст и как сайт отвечает роботу, знает только замер.
 *
 * `article_redirects` — следовать ли редиректу СТАТЬИ (у ленты и листинга редирект по-прежнему
 * исход, а не путь: t.me/s/<нет канала> → 302 → правдоподобная страница, §6.3.2):
 *   * `none` — 3xx статьи — отказ, как до 19.10.2026;
 *   * `same_host` (умолчание) — хоп только на хосты источника (`allowedHostsOf`: home_url,
 *     звенья цепочки, allow_hosts). TAdviser /a/NNN → /index.php/<заголовок>, openai.com
 *     /index/x → /index/x/;
 *   * `any_public` — хоп на любой публичный хост, как ссылка редактора (ПРАВИЛО 6.27).
 * На КАЖДОМ хопе ПРАВИЛО О-4 исполняется заново (схема, имя, резолв, непубличные диапазоны).
 *
 * `fetch_via` — чем ходить: `http` (NodeHttpFetcher), `browser` (лента и статьи — через
 * изолированный Chromium, `services/agent-monitor/src/crawl/browser-fetcher.ts`),
 * `browser_articles` (лента по http, статьи через Chromium — openai: лента отдаётся, статьи
 * за Cloudflare).
 */
export const ARTICLE_REDIRECT_POLICIES = ['none', 'same_host', 'any_public'] as const;
export const FETCH_VIA = ['http', 'browser', 'browser_articles'] as const;
export const MAX_REDIRECT_HOPS_CAP = 5;
export const MAX_REDIRECT_HOPS_DEFAULT = 3;

/**
 * Значение заголовка Accept-Language: токены языков, веса, запятые. Перевод строки и прочие
 * управляющие символы запрещены — значение уходит в заголовок запроса как есть.
 */
const AcceptLanguage = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9*][A-Za-z0-9*,;=. -]*$/, 'ожидался заголовок вида «en» или «ru,en;q=0.8»');

/**
 * Accept-Language строки: явное поле или вывод из `lang`. До 19.10.2026 каждый запрос нёс
 * «ru,en;q=0.8», и developers.google.com отвечал на статью 302 на `?hl=ru` (ревью №14).
 */
export function acceptLanguageOf(source: {
  lang: string;
  accept_language?: string | undefined;
}): string {
  if (source.accept_language !== undefined) return source.accept_language;
  return source.lang === 'en' ? 'en' : `${source.lang},en;q=0.8`;
}

/**
 * Есть ли у html-звена своя разметка. Без неё HtmlListingParser берёт каждый `a[href]`
 * страницы — навигацию, тарифы, PDF с куками — как недатированный «материал» (ревью №17).
 * Одно определение на схему реестра, бэкенд (флаг заглушки читается из parser_chain_json) и
 * сервис мониторинга (отказ включить заглушку отклонением).
 */
export function linkHasParseSpec(link: {
  parser: string;
  item_selector?: string | undefined;
  link_selector?: string | undefined;
  list_selector?: string | undefined;
}): boolean {
  if (link.parser !== 'html') return true;
  return (
    link.item_selector !== undefined ||
    link.link_selector !== undefined ||
    link.list_selector !== undefined
  );
}

/** Строка-заглушка: хотя бы одно html-звено без разметки. */
export function chainIsPlaceholder(
  chain: readonly Parameters<typeof linkHasParseSpec>[0][],
): boolean {
  return chain.some((l) => !linkHasParseSpec(l));
}

export const RegistryDefaultsSchema = z.strictObject({
  ...NETWORK_KNOBS,
  overlap_days: OverlapDays.default(OVERLAP_DAYS_DEFAULT),
});

export const SourceEntrySchema = z
  .strictObject({
    code: z.string().regex(SOURCE_CODE_RE, 'ожидался код вида ^[a-z][a-z0-9_-]{1,31}$'),
    title: z.string().min(1).max(80),
    home_url: SourceUrl,
    /** (0, 1]: входит в ранжирование множителем (§10.2). Ноль запрещён — источник с нулевым весом отключают, а не обнуляют. */
    weight: z.number().gt(0).lte(1),
    /**
     * Первоисточник или пересказ (`SOURCE_ORIGINS`). Свойство САЙТА, а не пары с брендом:
     * синхронизация пишет его в `source.origin`, и оно входит в `configHashOf` — двойник
     * `agi_<код>` обязан нести то же значение, что строка GeoBoost. Умолчание `media`: строки,
     * размеченные до 2026-11-02, разбираются как раньше и ссылки внутри текста не получают.
     */
    origin: SourceOriginSchema.default('media'),
    /** Отключённый источник остаётся строкой реестра: keep/replace — решение PR lead, не правка кода. */
    enabled: z.boolean().default(true),
    lang: z
      .string()
      .regex(/^[a-z]{2}$/, 'ожидался код языка ISO 639-1')
      .default('ru'),
    chain: z.array(SourceChainLinkSchema).min(1),
    /** true — фид несёт полный текст (MegaGroup), дозагрузка статьи не нужна. */
    body_in_feed: z.boolean().default(false),
    /** 'modified' (BotRank): pubDate — время правки, курсор двигается по fetched_at (§7.7). */
    date_semantics: z.enum(['published', 'modified']).default('published'),
    /** Обязательны при parser: rss — без них ПРАВИЛО 6.17 не проверить при утверждении реестра. */
    feed_depth_items: z.int().positive().optional(),
    feed_depth_days: z.int().positive().optional(),
    paywall: z.boolean().default(false),
    /** Маркеры антибот-заглушки конкретного источника — вход `accepts()` парсера. */
    challenge_markers: z.array(z.string().min(1)).default([]),
    /** Только tme_s: считать ли репосты материалами канала (§6.3.2, грабля 16.18). */
    include_forwards: z.boolean().default(false),
    /** Потолок пагинации за прогон (t.me/s/?before=, HTML-архив). */
    max_pages_per_run: z.int().positive().max(20).default(5),
    /**
     * Кросс-доменные адреса (CDN, редирект статьи) требуют явной записи — иначе
     * рантайм отобьёт их как `url_rejected` (ПРАВИЛО О-4). Неудобно ровно один раз
     * на источник и закрывает SSRF-класс целиком. Тот же список решает, чей элемент
     * ленты СВОЙ: элемент на хосте вне него обход выбрасывает (ПРАВИЛО 6.30).
     */
    allow_hosts: z.array(AllowedHost).default([]),
    /** Перекрытие окна этого источника, сутки; по умолчанию — `defaults.overlap_days`. */
    overlap_days: OverlapDays.optional(),
    timeout_ms: NETWORK_KNOBS.timeout_ms.optional(),
    max_bytes: NETWORK_KNOBS.max_bytes.optional(),
    min_interval_ms: NETWORK_KNOBS.min_interval_ms.optional(),
    max_requests_per_run: NETWORK_KNOBS.max_requests_per_run.optional(),
    min_article_chars: NETWORK_KNOBS.min_article_chars.optional(),
    fallback_charset: NETWORK_KNOBS.fallback_charset.optional(),
    /** Заголовок Accept-Language; умолчание выводит `acceptLanguageOf` из `lang`. */
    accept_language: AcceptLanguage.optional(),
    /**
     * false — статья не качается никогда, текст материала — то, что дала лента. Для
     * источников, чья лента самодостаточна, а страница пуста или закрыта (Google Search
     * Status: инцидент — 169 знаков в ленте, на странице тот же текст в обвязке).
     */
    article_fetch: z.boolean().default(true),
    article_redirects: z.enum(ARTICLE_REDIRECT_POLICIES).default('same_host'),
    max_redirect_hops: z.int().min(0).max(MAX_REDIRECT_HOPS_CAP).default(MAX_REDIRECT_HOPS_DEFAULT),
    /**
     * Порог длины материала этого источника вместо `brand.monitoring.min_material_chars`.
     * Для лент, где короткий текст — полный (статус-инциденты), а не тизер.
     */
    min_material_chars: z.int().positive().optional(),
    fetch_via: z.enum(FETCH_VIA).default('http'),
    /**
     * Строка-заглушка: адрес известен, разметка не снята. Ставится ровно тогда, когда у
     * html-звена нет селекторов, и такая строка не бывает включённой — ни файлом, ни
     * отклонением из интерфейса (ревью №17).
     */
    placeholder: z.boolean().default(false),
  })
  .check((ctx) => {
    const src = ctx.value;
    const isPlaceholderChain = chainIsPlaceholder(src.chain);
    if (isPlaceholderChain && !src.placeholder) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['placeholder'],
        message:
          'html-звено без item_selector/link_selector: это заглушка — поставьте placeholder: true ' +
          'и enabled: false, иначе листинг соберёт каждую ссылку страницы',
      });
    }
    if (!isPlaceholderChain && src.placeholder) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['placeholder'],
        message: 'placeholder: true при снятой разметке — флаг выводится из цепочки, уберите его',
      });
    }
    if (src.placeholder && src.enabled) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['enabled'],
        message: 'строка-заглушка не бывает включённой: сначала селекторы, замер, потом enabled',
      });
    }
    if (src.fetch_via !== 'http' && src.chain.some((l) => l.parser === 'tme_s')) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['fetch_via'],
        message:
          'fetch_via браузером не имеет смысла у tme_s: t.me/s/ отдаётся по http, пост — не статья',
      });
    }
    src.chain.forEach((link, i) => {
      if (link.parser === 'tme_s' && link.expect_prefix === undefined) {
        ctx.issues.push({
          code: 'custom',
          input: ctx.value,
          path: ['chain', i, 'expect_prefix'],
          message: 'expect_prefix обязателен при parser: tme_s (детект подмены канала, §6.3.2)',
        });
      }
      if (link.parser !== 'tme_s' && link.expect_prefix !== undefined) {
        ctx.issues.push({
          code: 'custom',
          input: ctx.value,
          path: ['chain', i, 'expect_prefix'],
          message: `expect_prefix запрещён при parser: ${link.parser}`,
        });
      }
      const htmlOnly = [
        link.list_selector,
        link.mode,
        link.item_selector,
        link.link_selector,
        link.title_selector,
        link.date_selector,
        link.lead_selector,
        link.date_attr,
      ];
      if (link.parser !== 'html' && htmlOnly.some((v) => v !== undefined)) {
        ctx.issues.push({
          code: 'custom',
          input: ctx.value,
          path: ['chain', i],
          message: `селекторы листинга и mode имеют смысл только при parser: html`,
        });
      }
    });
    if (src.chain.some((l) => l.parser === 'rss')) {
      for (const key of ['feed_depth_items', 'feed_depth_days'] as const) {
        if (src[key] === undefined) {
          ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: [key],
            message: `${key} обязателен для источника с parser: rss (ПРАВИЛО 6.17)`,
          });
        }
      }
    }
  });

export const SourceRegistrySchema = z
  .strictObject({
    schema_version: z.literal(1),
    brand: z.string().regex(BRAND_RE, 'ожидался код бренда вида ^[a-z][a-z0-9_-]{1,31}$'),
    defaults: RegistryDefaultsSchema,
    sources: z.array(SourceEntrySchema).min(1),
  })
  .check((ctx) => {
    // Повтор кода — это два обхода и две записи здоровья под одним ключом source.code.
    const seen = new Map<string, number>();
    ctx.value.sources.forEach((s, i) => {
      const first = seen.get(s.code);
      if (first === undefined) {
        seen.set(s.code, i);
        return;
      }
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        path: ['sources', i, 'code'],
        message: `код «${s.code}» повторяет sources[${first}]`,
      });
    });
  });

export type SourceRegistry = z.output<typeof SourceRegistrySchema>;
export type SourceRegistryEntry = z.output<typeof SourceEntrySchema>;
export type SourceChainLink = z.output<typeof SourceChainLinkSchema>;
